import { updateConfiguration } from '../../../../api/simple-project-service/configurations/updateConfiguration';
import { updatePublicConfiguration } from '../../../../api/simple-project-service/configurations/updatePublicConfiguration';
import { CameraAction } from '../../../../configurator/controllers/cameraController';
import { Article } from '../../../../interfaces/Article';
import { isSlotLine, Slot } from '../../../../interfaces/Slot';
import Store from '../../../../interfaces/Store';
import { ValidationAndLightingSetup, ViolationType } from '../../../../interfaces/Validation';
import { articleService } from '../../../../services/articleService';
import { MultipleViolationsError, validationService } from '../../../../services/validationService';
import { NotificationFactory } from '../../../../utils/factories/notificationFactory';
import { ActionType } from '../../../actions';
import { addNotifications } from '../../../notifications/notifications.actions';
import { selectArticle } from '../../../selection/selection.actions';
import {
  disableInteractions,
  enableInteractions,
} from '../../../suppressInteractions/suppressInteractions.actions';
import { updateDimensions } from '../../dimensions/dimensions.actions';
import { replaceArticleInConfiguration } from './replaceArticleInConfiguration';
import { loadRootDependentArticles } from './rootDependentArticles';

const REPLACE_ARTICLE = 'replaceArticle';

function handleProductCannotBeAddedError(dispatch, name) {
  dispatch(addNotifications([NotificationFactory.productCannotBeAddedAutomatically(name)]));
  dispatch(enableInteractions(REPLACE_ARTICLE));
}

function dispatchError(dispatch: any, error: Error) {
  if (error instanceof MultipleViolationsError) {
    dispatch(addNotifications([NotificationFactory.multipleViolations(error.violations)]));
  } else {
    dispatch(addNotifications([NotificationFactory.customNegative(error.message)]));
  }
  dispatch(enableInteractions(REPLACE_ARTICLE));
}

function hasUnfixableLightingError(
  validationAndLightingSetup: ValidationAndLightingSetup,
): boolean {
  if (validationAndLightingSetup.validation.unfixableErrors?.length > 0) {
    for (const unfixableError of validationAndLightingSetup.validation.unfixableErrors) {
      if (unfixableError.type === ViolationType.LIGHTING) {
        return true;
      }
    }
  }
  return false;
}

export const replaceArticle = (
  oldArticle: Article,
  newArticle: Article,
  newSlots: Slot[],
  childReplacement = false,
) => {
  return async (dispatch, getState: () => Store, { getScene, loggedIn }) => {
    try {
      dispatch(disableInteractions(REPLACE_ARTICLE));

      const replacingRoot = oldArticle.id === getState().configuration.root;

      newArticle.colors = oldArticle.colors;

      const attachedSlot = getState().configuration.getAttachedSlot(oldArticle.id);

      let newRequiredConnectArticle;
      const oldRequiredConnectArticleId = oldArticle.requiredConnectArticleId;
      if (oldRequiredConnectArticleId) {
        const newRequiredConnectArticleResponse = await articleService.getRequiredConnectArticle(
          newArticle.number,
          attachedSlot,
        );
        if (newRequiredConnectArticleResponse instanceof Error) {
          return handleProductCannotBeAddedError(dispatch, newArticle.definition.displayName);
        }
        newArticle.requiredConnectArticleId = newRequiredConnectArticleResponse.id;
        newRequiredConnectArticle = newRequiredConnectArticleResponse;
      }

      if (replacingRoot && oldArticle.definition.size !== newArticle.definition.size) {
        const dependentArticles = await loadRootDependentArticles(
          getState().configuration,
          newArticle,
          oldArticle,
        );
        for (const replacement of dependentArticles) {
          await dispatch(
            replaceArticle(
              replacement.oldArticle,
              replacement.newArticle,
              replacement.newSlots,
              true,
            ),
          );
        }
      }

      const futureConfiguration = await replaceArticleInConfiguration(
        getState().configuration,
        oldArticle.id,
        newArticle,
        newSlots,
        newRequiredConnectArticle,
      );

      const validationAndLightingSetup = await validationService.validateAndGetLightingSetup(
        futureConfiguration,
      );
      if (validationAndLightingSetup instanceof Error) {
        return dispatchError(dispatch, validationAndLightingSetup);
      }
      if (hasUnfixableLightingError(validationAndLightingSetup)) {
        return handleProductCannotBeAddedError(dispatch, newArticle.definition.displayName);
      }

      await dispatch({
        type: ActionType.REPLACE_ARTICLE,
        payload: {
          configuration: futureConfiguration,
          validationAndLightingSetup,
          setNewSelectedArticleId: !childReplacement && newArticle.id,
        },
      });

      const children = getState()
        .configuration.getChildren(newArticle.id)
        .map((childId) => {
          const slot = getState().configuration.getAttachedSlot(childId).definition;
          return { id: childId, slot };
        });

      replacingRoot
        ? await getScene().replaceRootArticle(
            oldArticle.id,
            newArticle,
            newSlots.map((slot) => slot.definition),
            children,
            getState().configuration.getPositionOfHeightControlPanel(),
          )
        : await getScene().replaceArticle(
            oldArticle.id,
            newArticle,
            newSlots.map((slot) => slot.definition),
            children,
            attachedSlot,
          );

      getState()
        .configuration.slots.filter(isSlotLine)
        .forEach((slot) => getScene().slotLineUtil.setNewSlotLineArrangement(slot, slot.children));

      getScene().cleanUpArticles(futureConfiguration.getAllArticles().map((article) => article.id));

      !childReplacement && (await dispatch(selectArticle(newArticle.id, CameraAction.RECENTER)));

      dispatch(updateDimensions());

      dispatch(enableInteractions(REPLACE_ARTICLE));

      if (!childReplacement) {
        loggedIn
          ? updateConfiguration(getState().configuration)
          : updatePublicConfiguration(getState().configuration);
      }
    } catch (e) {
      return handleProductCannotBeAddedError(dispatch, oldArticle.definition.displayName);
    }
  };
};
